/**
 * Gamification — port z js/gamification.js (1:1 logika).
 * Diamenty: day1=1, dayN>=2 = min((N-1)*10, 100). Streak resetuje się przy
 * przerwaniu dnia. lastClaimDate w formacie YYYY-MM-DD lokalny.
 */
import { doc, getDoc, runTransaction } from 'firebase/firestore';
import { db, IS_DEV_MODE } from '../../lib/firebase';

export interface UserStats {
  uid: string;
  familyId: string;
  displayName: string;
  streak: number;
  longestStreak: number;
  totalDiamonds: number;
  lastClaimDate: string | null;
  avatar: string | null;
}

const DEV_KEY = 'rf_dev_userstats';
const COLLECTION = 'userStats';

export function diamondsForDay(streak: number): number {
  if (streak <= 1) return 1;
  return Math.min((streak - 1) * 10, 100);
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

export function todayKey(): string {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function yesterdayKey(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function devLoadAll(): Record<string, UserStats> {
  try {
    return JSON.parse(localStorage.getItem(DEV_KEY) ?? '{}');
  } catch {
    return {};
  }
}

function devSaveAll(all: Record<string, UserStats>): void {
  localStorage.setItem(DEV_KEY, JSON.stringify(all));
}

function initialStats(uid: string, name: string, familyId: string): UserStats {
  return {
    uid,
    familyId,
    displayName: name,
    streak: 0,
    longestStreak: 0,
    totalDiamonds: 0,
    lastClaimDate: null,
    avatar: null,
  };
}

function computeNextStreak(stats: UserStats | null): number {
  if (!stats || !stats.lastClaimDate) return 1;
  if (stats.lastClaimDate === yesterdayKey()) return (stats.streak || 0) + 1;
  if (stats.lastClaimDate === todayKey()) return stats.streak || 1;
  return 1; // reset
}

export function canClaim(stats: UserStats | null): boolean {
  if (!stats) return true;
  return stats.lastClaimDate !== todayKey();
}

export function previewReward(stats: UserStats | null): { streak: number; diamonds: number } {
  const next = computeNextStreak(stats);
  return { streak: next, diamonds: diamondsForDay(next) };
}

export async function loadUserStats(uid: string, name: string, familyId: string): Promise<UserStats> {
  if (IS_DEV_MODE) {
    return devLoadAll()[uid] ?? initialStats(uid, name, familyId);
  }
  try {
    const snap = await getDoc(doc(db, COLLECTION, uid));
    return snap.exists() ? (snap.data() as UserStats) : initialStats(uid, name, familyId);
  } catch {
    return initialStats(uid, name, familyId);
  }
}

export interface ClaimResult {
  alreadyClaimed: boolean;
  reward: number;
  stats: UserStats;
}

export async function claim(uid: string, name: string, familyId: string): Promise<ClaimResult> {
  if (IS_DEV_MODE) {
    const all = devLoadAll();
    const cur = all[uid] ?? initialStats(uid, name, familyId);
    if (!canClaim(cur)) return { alreadyClaimed: true, reward: 0, stats: cur };
    const newStreak = computeNextStreak(cur);
    const reward = diamondsForDay(newStreak);
    const updated: UserStats = {
      ...cur,
      uid,
      familyId,
      displayName: name,
      streak: newStreak,
      longestStreak: Math.max(cur.longestStreak ?? 0, newStreak),
      totalDiamonds: (cur.totalDiamonds ?? 0) + reward,
      lastClaimDate: todayKey(),
    };
    all[uid] = updated;
    devSaveAll(all);
    return { alreadyClaimed: false, reward, stats: updated };
  }
  const ref = doc(db, COLLECTION, uid);
  let reward = 0;
  let alreadyClaimed = false;
  let statsOut: UserStats = initialStats(uid, name, familyId);
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(ref);
    const cur = snap.exists() ? (snap.data() as UserStats) : initialStats(uid, name, familyId);
    if (!canClaim(cur)) {
      alreadyClaimed = true;
      statsOut = cur;
      return;
    }
    const newStreak = computeNextStreak(cur);
    reward = diamondsForDay(newStreak);
    statsOut = {
      ...cur,
      uid,
      familyId,
      displayName: name,
      streak: newStreak,
      longestStreak: Math.max(cur.longestStreak ?? 0, newStreak),
      totalDiamonds: (cur.totalDiamonds ?? 0) + reward,
      lastClaimDate: todayKey(),
    };
    tx.set(ref, statsOut);
  });
  return { alreadyClaimed, reward, stats: statsOut };
}
